//
// # Layout Pin Context Menu
//
// Displays actions when right-clicking on a layout pin.

import type { Editor } from "./editor";
import { logger } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { layout } from "GenAlphaModel";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";

export class LayoutPinContextMenu extends BaseContextMenu {
  private currentPinData: layout.LayoutPin | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "140px",
    maxWidth: "250px",
  };

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header-light {
        padding: 6px 12px;
        color: ${p.textTarget};
        font-size: 11px;
      }
    `;
  }

  protected clearState(): void {
    this.currentPinData = null;
  }

  // Show context menu for a layout pin at the given page position.
  show = (pinData: layout.LayoutPin, pageX: number, pageY: number): void => {
    this.editor.hideAllContextMenus();
    this.currentPinData = pinData;
    this.ensureMenuElement();

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header-light">Pin: ${pinData.name}</div>`;
    content += `</div>`;
    content += `<div class="context-menu-separator"></div>`;
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="edit-pin">Edit Pin...</div>`;
    content += `<div class="context-menu-item" data-action="delete-pin">Delete</div>`;
    content += `</div>`;

    this.displayMenu(content, pageX, pageY);
    this.setupMenuItemHandlers();
  };

  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      const element = item as HTMLElement;
      element.onclick = (e) => {
        e.stopPropagation();
        const action = element.getAttribute("data-action");

        if (action === "edit-pin") {
          this.handleEditPin();
        } else if (action === "delete-pin") {
          this.handleDeletePin();
        }

        this.hide();
      };
    });
  };

  private handleEditPin = (): void => {
    if (!this.currentPinData) return;
    const { name, pinType, id } = this.currentPinData;
    if (id === undefined) return;
    // The dialog edits one layer: a pin with none or several shapes has none.
    const layer = schema.pinLayer(this.currentPinData);
    if (layer.err) {
      logger.warn(`[LayoutPinContextMenu] Cannot edit: ${layer.val.message}`);
      return;
    }
    this.editor.platform.notifyEditLayoutPin({
      name,
      layer: layer.val,
      pinType,
      id,
    });
  };

  private handleDeletePin = (): void => {
    const { editor } = this;
    const result = editor.content.deleteSelected();
    if (result) editor.showItemFeedback("Deleted", 1);
  };
}
